/* React */
import { useEffect } from 'react'
import { useStore } from 'react-redux'

/* Redux */
import { useDispatch, useSelector } from 'src/redux/hooks'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { 
  tItems,
} from 'src/shared/frontendTypes/warenkorb'
import {
  isStorageKasse
} from 'src/shared/frontendTypes/kasse'
import { 
  isItem as isWunschlisteItem,
  isItems as isWunschlisteItems,
} from 'src/shared/frontendTypes/wunschliste'
import { tcouponFrontend } from 'src/shared/frontendTypes/coupon'
import { refreshCartItemsCommerce } from 'src/functions/refreshCartItemsCommerce'
import { refreshCartIfCurrent } from 'src/functions/cartRefreshGuard'
import type { RootState } from './reducer'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

const REFRESH_INTERVAL_MS = 30000

const isRestorableCoupon = (value: unknown): value is tcouponFrontend => {
  if (!value || typeof value !== 'object') return false

  const couponValue = value as Partial<tcouponFrontend>
  return Boolean(
    typeof couponValue.code === 'string'
    && couponValue.code.trim()
    && typeof couponValue.balance === 'string'
    && couponValue.balance.trim(),
  )
}

const CheckLocalStorage = () => {
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const warenkorbItems = useSelector(store => store.Warenkorb.items)
  const coupon = useSelector(store => store.Warenkorb.coupon)
  const couponObjekt = useSelector(store => store.Warenkorb.couponObjekt)

  const restoreWarenkorb = (
    items: tItems,
    couponCode: string | null,
    couponObject: tcouponFrontend | null,
  ) => {
    dispatch({
      type: 'restoreWarenkorbItems',
      payload: {
        items,
        coupon: couponCode,
        couponObjekt: couponObject,
      },
    })
  }

  const refreshWarenkorb = async (
    items: tItems,
    couponCode: string | null,
    couponObject: tcouponFrontend | null,
  ) => {
    if (!items || Object.keys(items).length === 0) return

    const initialState = store.getState().Warenkorb
    if (initialState.items !== items || initialState.coupon !== couponCode
      || initialState.couponObjekt !== couponObject) return

    await refreshCartIfCurrent({
      initialState,
      getCurrentState: () => store.getState().Warenkorb,
      refresh: () => refreshCartItemsCommerce(items),
      apply: refreshedItems => {
        if (JSON.stringify(refreshedItems) === JSON.stringify(items)) return
        restoreWarenkorb(refreshedItems, couponCode, couponObject)
      },
    })
  }

  const loadUser = () => {
    const storedUser = localStorage.getItem('user')
    if (!storedUser) { dispatch({ type: 'logout' }); return }
    const user = JSON.parse(storedUser)
    dispatch({ type: 'login', payload: user })
  }

  const loadWarenkorb = () => {
    // Versuch, den Eintrag "warenkorb" aus dem LocalStorage zu lesen
    const storedWarenkorb = localStorage.getItem('warenkorb')
    if (!storedWarenkorb) return
    
    const parsed = JSON.parse(storedWarenkorb)
  
    // 1. Prüfe, ob der Eintrag überhaupt ein Objekt ist
    if (!parsed || typeof parsed !== 'object') return
  
    // 2. Prüfe, ob parsed ein "items"-Feld enthält und ob dieses valide ist
    if (!('items' in parsed)) return
  
    // 3. Fallback für coupon und couponObjekt
    const storedCouponCode = typeof parsed.coupon === 'string' && parsed.coupon.trim()
      ? parsed.coupon
      : null
    const couponObj = isRestorableCoupon(parsed.couponObjekt)
      ? parsed.couponObjekt
      : null
    const couponCode = couponObj?.code || storedCouponCode
    const items = parsed.items as tItems

    restoreWarenkorb(items, couponCode, couponObj)
    void refreshWarenkorb(items, couponCode, couponObj)
  }

  const loadKasse = () => {
    const storedKasse = localStorage.getItem('kasse')
    if (!storedKasse) return
    const kasse:unknown = JSON.parse(storedKasse)
    if (!isStorageKasse(kasse)) return
    dispatch({ type: 'restoreKasse', payload: kasse })
  }

  const loadWunschliste = () => {
    const storedWunschliste = localStorage.getItem('wunschliste')
    if (!storedWunschliste) return
    const wunschliste:unknown = JSON.parse(storedWunschliste)
    if (
      !wunschliste
      || typeof wunschliste !== 'object'
      || !hasOwnProperty(wunschliste, 'items')
      || !isWunschlisteItems(wunschliste.items)
      || !Object.values(wunschliste.items).every(item=>isWunschlisteItem(item))
      ) return
    dispatch({ type: 'restoreWunschlisteItems', payload: wunschliste.items })
  }

  const loadAll = () => {
    loadUser()
    loadWarenkorb()
    loadKasse()
    loadWunschliste()
  }

  const onFocus = () => loadAll()

  useEffect(() => {
    loadAll()
    window.addEventListener('focus', onFocus, { passive: true })

    return () => {
      window.removeEventListener('focus', onFocus)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const refreshInterval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      void refreshWarenkorb(warenkorbItems, coupon, couponObjekt)
    }, REFRESH_INTERVAL_MS)

    return () => window.clearInterval(refreshInterval)
  }, [warenkorbItems, coupon, couponObjekt])

  return null
}

export default CheckLocalStorage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
